import { useMemo } from 'react';
import { AbilityBuilder, AnyAbility, createMongoAbility } from '@casl/ability';
import {
  parsePermissionString,
  AnyPermissionString,
  createAbilityHelpers,
  Conditions,
} from '@nohara/oidc-core';

import { useOidc } from '../providers/OidcProvider';

export interface UseCanAccessProps {
  requireAll?: AnyPermissionString[];
  requireAny?: AnyPermissionString[];
  grantSelf?: AnyPermissionString[];
  context?: Conditions;
}

export const useCanAccess = ({ requireAll, requireAny, grantSelf, context }: UseCanAccessProps) => {
  const { isAuthenticated, isLoading, ability, userId } = useOidc();

  const selfAbility = useMemo(() => {
    if (!ability) {
      return undefined;
    }

    const abilityBuilder = new AbilityBuilder<AnyAbility>(createMongoAbility);

    grantSelf?.forEach((permission) => {
      const { action, subject, conditions } = parsePermissionString(permission);
      abilityBuilder.can(action, subject, {
        ...conditions,
        userId,
      });
    });

    abilityBuilder.rules.push(...ability.rules);

    return abilityBuilder.build();
  }, [userId, grantSelf, ability]);

  return useMemo(() => {
    if (!selfAbility) {
      return {
        isLoading,
        isAuthenticated,
        canAccess: false,
        canAccessAll: false,
        canAccessAny: false,
      };
    }

    const abilityHelpers = createAbilityHelpers(selfAbility, context);

    const canAccessAll = requireAll ? abilityHelpers.hasAllPermissions(requireAll) : true;
    const canAccessAny = requireAny ? abilityHelpers.hasAnyPermission(requireAny) : true;

    return {
      isLoading,
      isAuthenticated,
      canAccess: isAuthenticated && canAccessAll && canAccessAny,
      canAccessAll,
      canAccessAny,
    };
  }, [selfAbility, context, requireAll, requireAny, isLoading, isAuthenticated]);
};
